import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { z } from 'zod';
/**
 * Sanitize schema for agent-authored markdown: same as rehype-sanitize's
 * default (strips `<script>`, event handlers, etc.) except the URL protocol
 * allowlist is narrowed to match {@link import('../lib/safe-url.js').sanitizeHref} —
 * `http`, `https`, `mailto` only. Images may not use `mailto:`.
 *
 * Exported so {@link import('../lib/inline-markdown.js').InlineMarkdown} can
 * apply the exact same sanitization to inline digest strings.
 */
export const MARKDOWN_SANITIZE_SCHEMA = {
    ...defaultSchema,
    protocols: {
        ...defaultSchema.protocols,
        href: ['http', 'https', 'mailto'],
        src: ['http', 'https'],
    },
};
export const MarkdownPropsSchema = z
    .object({
    body: z.string(),
})
    .strict();
// Explicit per-element styling rather than the Tailwind Typography plugin's
// `prose` classes — this package can't assume a consuming app has that
// plugin installed. All colors resolve to shadcn/ui-style theme tokens, so
// they adapt to light/dark without `dark:` variants.
const MARKDOWN_COMPONENTS = {
    p: ({ children }) => (_jsx("p", { className: "mb-2 text-sm leading-relaxed text-foreground/85", children: children })),
    h1: ({ children }) => (_jsx("h1", { className: "mb-2 mt-4 text-lg font-semibold text-foreground", children: children })),
    h2: ({ children }) => (_jsx("h2", { className: "mb-2 mt-3 text-base font-semibold text-foreground", children: children })),
    h3: ({ children }) => (_jsx("h3", { className: "mb-1 mt-3 text-sm font-semibold text-foreground", children: children })),
    ul: ({ children }) => (_jsx("ul", { className: "mb-2 list-disc space-y-1 pl-5 text-sm text-foreground/85", children: children })),
    ol: ({ children }) => (_jsx("ol", { className: "mb-2 list-decimal space-y-1 pl-5 text-sm text-foreground/85", children: children })),
    li: ({ children }) => _jsx("li", { children: children }),
    strong: ({ children }) => _jsx("strong", { className: "font-semibold text-foreground", children: children }),
    code: ({ children }) => (_jsx("code", { className: "rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground", children: children })),
    pre: ({ children }) => (_jsx("pre", { className: "mb-2 overflow-x-auto rounded bg-muted p-2 text-xs text-foreground", children: children })),
    blockquote: ({ children }) => (_jsx("blockquote", { className: "mb-2 border-l-2 border-border pl-3 text-sm italic text-muted-foreground", children: children })),
    hr: () => _jsx("hr", { className: "my-3 border-border" }),
    table: ({ children }) => (_jsx("div", { className: "mb-2 overflow-x-auto", children: _jsx("table", { className: "w-full border-collapse text-left text-sm", children: children }) })),
    th: ({ children }) => (_jsx("th", { className: "border-b border-border px-2 py-1 font-semibold text-foreground", children: children })),
    td: ({ children }) => (_jsx("td", { className: "border-b border-border/50 px-2 py-1 text-foreground/85", children: children })),
    a: ({ href, children }) => (_jsx("a", { href: href, target: "_blank", rel: "noopener noreferrer nofollow", className: "text-primary underline underline-offset-4 hover:no-underline", children: children })),
};
/** Image placeholders disclose saved content without fetching remote URLs before approval. */
function PreviewImage({ alt, src }) {
    return (_jsxs("span", { children: [alt || 'Image', " (loads after saving: ", src || 'no URL', ")"] }));
}
const PREVIEW_COMPONENTS = { ...MARKDOWN_COMPONENTS, img: PreviewImage };
/**
 * Renders a `markdown` block. Sanitized GitHub-flavored markdown —
 * never `dangerouslySetInnerHTML`. Agent-supplied link/image URLs are
 * restricted to `http:`/`https:`/`mailto:` via the rehype-sanitize schema.
 */
export function MarkdownPrimitive({ body, imageMode = 'load', }) {
    return (_jsx("div", { className: "max-w-none", children: _jsx(Markdown, { remarkPlugins: [remarkGfm], rehypePlugins: [[rehypeSanitize, MARKDOWN_SANITIZE_SCHEMA]], components: imageMode === 'placeholder' ? PREVIEW_COMPONENTS : MARKDOWN_COMPONENTS, children: body }) }));
}
